import { KeyboardArrowRight } from "@mui/icons-material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { memo } from "react";
import { theme } from "theme";
import { ColumnData, currencyOf, isChecked } from "utils/util";

export type TCustomTableCollapsableProps<T extends { uuid: string }, C = T[keyof T] | null> = {
    item: T;
    colapsadas: Array<string>;
    onToggle: (colapsadas: Array<string>, uuid: string) => void;
    columns: Array<ColumnData<T>>;
    children: C;
    columnsChildren: Array<ColumnData<C extends Array<infer U> ? U : C>>;
    toFilter: string;
    childrenTitle?: string;
};

const getValue = (obj: any, path?: string) => {
    if (!path) return "";
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const normalizeValue = (v: any) => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "number") return v.toString();
    if (typeof v === "string") return v;
    return String(v);
};

const formatColumnValue = (col: ColumnData<any>, value: any) => {
    if (value === null || value === undefined) return "-";

    switch (col.type) {
        case "currency":
            return currencyOf(value as number);

        case "unidadeMedida":
            return `${value} ${col.unidadeMedida ?? ""}`.trim();

        case "percent":
            return `${Number(value).toFixed(2).replace(".", ",")}`;

        case "number":
            return Number(value).toString();

        case "string":
        default:
            return normalizeValue(value);
    }
};


const CustomTableCollapsable = (props: TCustomTableCollapsableProps<any, any>) => {
    const { item, colapsadas, onToggle } = props;

    return (
        <TableRow sx={{ "& > *": { borderBottom: "unset" }, width: "100%", display: "grid" }}>
            <TableCell style={{ padding: 2, border: 0 }} colSpan={999}>
                <Table sx={{ width: "100%", padding: 0 }}>
                    <TableBody>

                        <TableRow sx={{ width: "100%" }}>
                            {props.columns.map((col, index) => {
                                const value = getValue(item, col.key);

                                return (
                                    <TableCell
                                        key={col.id}
                                        sx={{
                                            borderBottom: isChecked(colapsadas, item.uuid) ? 0 : undefined,
                                            paddingLeft: index === 0 ? "10px" : undefined,
                                            paddingRight: col.textAlign === "right" ? "15px" : undefined,
                                            width: col.width,
                                            minWidth: col.width,
                                            maxWidth: col.width
                                        }}
                                        padding="none"
                                        align={col.textAlign}
                                    >
                                        {/* Botão de expandir */}
                                        {index === 0 && props.children !== null && (
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => onToggle(colapsadas, item.uuid)}
                                                sx={{ height: "10px" }}
                                            >
                                                {isChecked(colapsadas, item.uuid)
                                                    ? <KeyboardArrowDown />
                                                    : <KeyboardArrowRight />}
                                            </IconButton>
                                        )}
                                        {formatColumnValue(col, value)}
                                    </TableCell>
                                );
                            })}
                        </TableRow>

                        {/* Área colapsável */}
                        {props.children !== null && (
                            <TableRow sx={{ "& > *": { borderBottom: "unset" }, bgcolor: theme.palette.background.paper }}>
                                <TableCell style={{ padding: 0 }} colSpan={999}>
                                    <Collapse in={isChecked(colapsadas, item.uuid)} timeout="auto" unmountOnExit>

                                        {props.childrenTitle &&
                                            <Box sx={{ textAlign: "center", my: 2 }}>
                                                <Typography sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
                                                    {props.childrenTitle}
                                                </Typography>
                                            </Box>
                                        }

                                        {/* 📌 Somente os campos definidos em columnsChildren */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent: "flex-start",
                                                gap: "35px",
                                                padding: "10px",
                                                overflowX: "auto"
                                            }}
                                        >
                                            {props.columnsChildren.map((col) => {
                                                const value = getValue(props.children, col.key);

                                                return (
                                                    <Box
                                                        key={col.id}
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            width: col.width ?? "140px",
                                                            textAlign: col.textAlign
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: "bold",
                                                                fontSize: "11px",
                                                                textTransform: "capitalize",
                                                                color: theme.palette.primary.main
                                                            }}
                                                        >
                                                            {col.header}
                                                        </Typography>

                                                        <Typography sx={{ fontSize: "13px" }}>
                                                            {col.type === "currency"
                                                                ? currencyOf(value)
                                                                : normalizeValue(value)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        )}

                    </TableBody>
                </Table>
            </TableCell>
        </TableRow>
    );
};

export default memo(CustomTableCollapsable);
